/**
 * Catalog right-pane "POS" section: the two POS-only article settings —
 * the on-site bottle pfand (auto-adds a deposit line at the till) and an
 * optional POS-specific sell price (overrides the kiosk price at the POS only).
 *
 * Mirrors PfandEditor / PackSizeEditor; persists via the per-field PATCH
 * routes. Kept under components/pos/ so it stays grouped with the POS surface.
 */

import { useEffect, useState } from "react";

import { Alert, Button, InputNumber, Select, Space, Typography } from "antd";

import { ApiError, api, type PfandValueRow } from "../../api/client";
import { formatCents } from "../../utils/dateFormat";

const { Text } = Typography;

interface Props {
  chainRootId: number;
  onsitePfandValueId: number | null;
  posPriceCents: number | null;
  onSaved: () => void;
}

export function PosArticleConfig({
  chainRootId,
  onsitePfandValueId,
  posPriceCents,
  onSaved,
}: Props) {
  const [onsiteOptions, setOnsiteOptions] = useState<PfandValueRow[] | null>(
    null,
  );
  const [savingPfand, setSavingPfand] = useState(false);
  const [savingPrice, setSavingPrice] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Only ONSITE-kind pfand values are valid on-site bottle deposits.
  useEffect(() => {
    let cancelled = false;
    void api
      .listPfandValues(true, "onsite")
      .then((rows) => {
        if (!cancelled) setOnsiteOptions(rows);
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof ApiError ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const saveOnsite = async (next: number | null): Promise<void> => {
    setErr(null);
    setSavingPfand(true);
    try {
      await api.assignArticleOnsitePfand(chainRootId, next);
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.detail : String(e));
    } finally {
      setSavingPfand(false);
    }
  };

  // POS price draft (euros in the input; cents on the wire). null = no override.
  const [priceDraft, setPriceDraft] = useState<number | null>(
    posPriceCents === null ? null : posPriceCents / 100,
  );
  useEffect(() => {
    setPriceDraft(posPriceCents === null ? null : posPriceCents / 100);
  }, [posPriceCents]);
  const draftCents =
    priceDraft === null || priceDraft === undefined
      ? null
      : Math.round(priceDraft * 100);
  const priceDirty = draftCents !== posPriceCents;

  const savePrice = async (): Promise<void> => {
    setErr(null);
    setSavingPrice(true);
    try {
      await api.assignArticlePosPrice(chainRootId, draftCents);
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.detail : String(e));
    } finally {
      setSavingPrice(false);
    }
  };

  return (
    <Space direction="vertical" size={10} style={{ width: "100%" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Text type="secondary" style={{ minWidth: 90 }}>
          On-site pfand
        </Text>
        <Select<number | null>
          value={onsitePfandValueId}
          placeholder="No on-site deposit"
          style={{ flex: 1, maxWidth: 280 }}
          allowClear
          loading={onsiteOptions === null || savingPfand}
          disabled={onsiteOptions === null}
          options={(onsiteOptions ?? []).map((p) => ({
            value: p.id,
            label: `${p.label} (${formatCents(p.cents)})${p.active ? "" : " (archived)"}`,
          }))}
          onChange={(v) => void saveOnsite((v as number | null) ?? null)}
        />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Text type="secondary" style={{ minWidth: 90 }}>
          POS price
        </Text>
        <Space.Compact style={{ flex: 1, maxWidth: 280 }}>
          <InputNumber
            style={{ flex: 1 }}
            min={0}
            step={0.1}
            prefix="€"
            placeholder="kiosk price"
            value={priceDraft ?? undefined}
            onChange={(v) =>
              setPriceDraft(v === null || v === undefined ? null : Number(v))
            }
          />
          <Button
            type="primary"
            disabled={!priceDirty}
            loading={savingPrice}
            onClick={() => void savePrice()}
          >
            Save
          </Button>
        </Space.Compact>
      </div>
      <Text type="secondary" style={{ fontSize: 11 }}>
        On-site pfand auto-adds a deposit line at the POS. POS price overrides the
        kiosk price at the till only — leave blank to sell at the kiosk price.
      </Text>
      {err && <Alert type="error" message={err} showIcon />}
    </Space>
  );
}

export default PosArticleConfig;
